/**
 * AnnouncementSlider Web Component
 * Handles multi-announcement carousel with Swiper.js
 */
class AnnouncementSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.observer = null;
  }

  connectedCallback() {
    this.initSlider();
    this.initHeightObserver();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  initSlider() {
    const swiperContainer = this.querySelector(".announcement-bar__swiper");
    if (!swiperContainer) return;

    // Get configuration from data attributes
    const autoplay = this.dataset.autoplay === "true";
    const delay = parseFloat(this.dataset.delay) * 1000;
    const speed = parseFloat(this.dataset.speed) * 1000;
    const pauseOnHover = this.dataset.pauseHover === "true";
    const showArrows = this.dataset.showArrows === "true";
    const slideCount = parseInt(this.dataset.slideCount) || 1;

    // Only initialize slider if there's more than one slide
    if (slideCount <= 1) return;

    // Configure autoplay
    let autoplayConfig = false;
    if (autoplay && slideCount > 1) {
      autoplayConfig = {
        delay: delay,
        disableOnInteraction: false,
        pauseOnMouseEnter: pauseOnHover,
      };
    }

    // Configure navigation
    const navigationConfig = showArrows
      ? {
          nextEl: this.querySelector(".swiper-button-next"),
          prevEl: this.querySelector(".swiper-button-prev"),
        }
      : false;

    // Initialize Swiper
    this.swiper = new Swiper(swiperContainer, {
      loop: slideCount > 1,
      speed: speed,
      autoplay: autoplayConfig,
      navigation: navigationConfig,
      effect: "slide",
      allowTouchMove: true,
      grabCursor: true,
      slidesPerView: 1,
      centeredSlides: true,
      on: {
        init: () => {
          // Trigger height recalculation after init
          this.updateHeight();
        },
        slideChange: () => {
          this.updateHeight();
        },
      },
    });
  }

  initHeightObserver() {
    const annBar = document.querySelector(".section-announcement");
    if (!annBar) return;

    // Create intersection observer for sticky header calculations
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const annBarHeight = annBar?.getBoundingClientRect().height || 0;
            document.documentElement.style.setProperty(
              "--ann-height",
              `${(annBarHeight * entry.intersectionRatio).toFixed(2)}px`
            );
          } else {
            document.documentElement.style.setProperty("--ann-height", "0px");
          }
        });
      },
      {
        threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
      }
    );

    this.observer.observe(annBar);
  }

  updateHeight() {
    // Trigger a reflow to update CSS variable
    const annBar = document.querySelector(".section-announcement");
    if (annBar) {
      const height = annBar.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--ann-height", `${height}px`);
    }
  }

  // Method to reinitialize slider (useful for Shopify section events)
  reinit() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    this.initSlider();
    this.initHeightObserver();
  }
}

// Register custom element
if (!customElements.get("announcement-slider")) {
  customElements.define("announcement-slider", AnnouncementSlider);
}

// Handle Shopify section events
(function () {
  const handleSectionEvents = () => {
    const slider = document.querySelector("announcement-slider");
    if (slider && slider.reinit) {
      slider.reinit();
    }
  };

  document.addEventListener("shopify:section:load", handleSectionEvents);
  document.addEventListener("shopify:section:unload", handleSectionEvents);
  document.addEventListener("shopify:section:reorder", handleSectionEvents);
})();
